import React, { useState } from "react";
import { SHOWCASE } from "@/constants/testIds";

const tabs = [
  {
    id: "flow",
    label: "Insurance Claim Flow",
    testId: SHOWCASE.tabFlow,
    sub: "session amount → collected",
    img: `${process.env.PUBLIC_URL}/images/claim-flow.webp`,
    description:
      "A live diagram of every dollar in flight — from session amount through billed, accepted, forwarded, underpaid and collected. No more reconciling reports across systems.",
  },
  {
    id: "sessions",
    label: "Sessions by Claim Status",
    testId: SHOWCASE.tabSessions,
    sub: "donut + breakdown",
    img: `${process.env.PUBLIC_URL}/images/sessions.png`,
    description:
      "Spot what's stuck. Forwarded, underpaid, not submitted, accepted — see counts and dollars by status with live filters across billing co, practice and insurance.",
  },
  {
    id: "aging",
    label: "Aging Report",
    testId: SHOWCASE.tabAging,
    sub: "0–7d · 8–14d · 15–30d · 31–60d · 61–90d · 90+d",
    img: `${process.env.PUBLIC_URL}/images/aging.png`,
    description:
      "Aging that updates as claims move. Toggle between session date and last claim status change to surface the queues that need work today.",
  },
  {
    id: "month",
    label: "Claims by Month & Status",
    testId: SHOWCASE.tabMonth,
    sub: "monthly performance",
    img: `${process.env.PUBLIC_URL}/images/month.png`,
    description:
      "Track collected vs billed vs not-submitted by service month. Catch month-over-month drift before it becomes a write-off.",
  },
];

export const ProductShowcase = () => {
  const [active, setActive] = useState(tabs[0].id);
  const current = tabs.find((t) => t.id === active);

  return (
    <section
      id="product"
      data-testid={SHOWCASE.section}
      className="relative py-24 md:py-32 bg-[#F8F9FA] border-y border-zinc-200"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="font-mono-label text-[10px] text-[#0047FF]">
              // Inside the product
            </span>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-950">
              Built for the people who actually work the claims.
            </h2>
            <p className="mt-5 text-lg text-zinc-600">
              Every screen is filterable by billing co, practice and insurance — so
              executives, billers and clinicians see the exact slice they need.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={t.testId}
              onClick={() => setActive(t.id)}
              className={`px-4 h-10 rounded-full border text-sm transition-all ${
                active === t.id
                  ? "bg-zinc-950 text-white border-zinc-950"
                  : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Active */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            <span className="font-mono-label text-[10px] text-zinc-500">
              {current.sub}
            </span>
            <h3 className="font-display mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950">
              {current.label}
            </h3>
            <p className="mt-4 text-base text-zinc-600 leading-relaxed">
              {current.description}
            </p>

            <ul className="mt-6 space-y-3 text-sm text-zinc-700">
              <li className="flex gap-3">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#0047FF]" />
                Filter by Billing Co · Practice · Insurance
              </li>
              <li className="flex gap-3">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#0047FF]" />
                Live refresh — no nightly batch lag
              </li>
              <li className="flex gap-3">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#0047FF]" />
                Drill into any number to its underlying claims
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_30px_80px_-30px_rgba(9,9,11,0.25)] overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-zinc-200 bg-zinc-50">
                <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
                <div className="ml-3 font-mono-label text-[10px] text-zinc-500">
                  zaplee.ai / {current.id}
                </div>
              </div>
              <img
                key={current.id}
                data-testid={SHOWCASE.image}
                src={current.img}
                alt={current.label}
                className="w-full block rise"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
